import type { Point as HighchartsPoint } from "highcharts";
import ReactDOMServer from "react-dom/server";

import { CircularLoader } from "@similarweb/ui-components";

import { TrendCell } from "app/components/React/Table/cells";
import { ShareTooltip } from "app/components/ShareTooltip/ShareTooltip";
import { translate } from "app/filters/i18nFilter";
import { MetricFormat } from "app/common/benchmark/types";
import { FORMATTERS, PERCENTAGE } from "app/common/benchmark/formatters";
import type { PrimaryEntityMetadata } from "app/pages/benchmark-tracker/types/api.types";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import {
    LoaderWrapper,
    ShareBlockContainer,
    ShareValue,
    StyledText,
    TrendsBlockContainer,
    ValueContainer,
} from "./styles";

type ShareBlockSchema = {
    shareTrend: PrimaryEntityMetadata["shareTrend"];
    percentageValue: PrimaryEntityMetadata["percentageValue"];
    isLoading: boolean;
};

export const ShareBlock = ({ shareTrend, percentageValue, isLoading }: ShareBlockSchema) => {
    const value = FORMATTERS[MetricFormat.percent](percentageValue);
    const { granularity } = proNavigator.getParams();

    const TrendCellConfig = {
        chart: { height: 64 },
        tooltip: {
            enabled: true,
            outside: true,
            useHTML: true,
            border: false,
            shadow: false,
            backgroundColor: "transparent",
            formatter(this: HighchartsPoint) {
                return ReactDOMServer.renderToString(
                    <ShareTooltip
                        timestamp={this.x}
                        value={this.y ?? 0}
                        granularity={granularity}
                        formatter={FORMATTERS[MetricFormat.percentSign]}
                    />,
                );
            },
        },
    };

    const row = { field: "field" };

    return (
        <ShareBlockContainer>
            {isLoading ? (
                <LoaderWrapper>
                    <CircularLoader isLarge />
                </LoaderWrapper>
            ) : (
                <>
                    <ValueContainer>
                        <ShareValue>
                            {value}
                            {PERCENTAGE}
                        </ShareValue>
                        <StyledText>{translate("bt.metrics.myShare.text")}</StyledText>
                    </ValueContainer>

                    {shareTrend ? (
                        <TrendCell
                            value={shareTrend}
                            row={row}
                            Container={TrendsBlockContainer}
                            config={TrendCellConfig}
                        />
                    ) : null}
                </>
            )}
        </ShareBlockContainer>
    );
};
